/**
 * ScheduleReplicationModal Component
 * Modal for replicating schedules from one professional to others
 *
 * @version 2025.1.0
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Chip,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import {
  ProfessionalForSchedule,
  ClinicForSchedule,
  ScheduleReplicationData,
} from '../../../types/professionalSchedule';

interface ScheduleReplicationModalProps {
  open: boolean;
  onClose: () => void;
  onReplicate: (data: ScheduleReplicationData) => Promise<void>;
  sourceProfessional: ProfessionalForSchedule;
  availableProfessionals: ProfessionalForSchedule[];
  clinic: ClinicForSchedule;
}

const ScheduleReplicationModal: React.FC<ScheduleReplicationModalProps> = ({
  open,
  onClose,
  onReplicate,
  sourceProfessional,
  availableProfessionals,
  clinic,
}) => {
  const [selectedProfessionals, setSelectedProfessionals] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter out the source professional
  const targetProfessionals = availableProfessionals.filter(
    p => p.id !== sourceProfessional.id
  );

  const handleToggle = (professionalId: number) => {
    const currentIndex = selectedProfessionals.indexOf(professionalId);
    const newSelected = [...selectedProfessionals];

    if (currentIndex === -1) {
      newSelected.push(professionalId);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    setSelectedProfessionals(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedProfessionals.length === targetProfessionals.length) {
      setSelectedProfessionals([]);
    } else {
      setSelectedProfessionals(targetProfessionals.map(p => p.id));
    }
  };

  const handleReplicate = async () => {
    if (selectedProfessionals.length === 0) {
      setError('Selecione pelo menos um profissional');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      await onReplicate({
        sourceProfessionalId: sourceProfessional.id,
        targetProfessionalIds: selectedProfessionals,
        clinicId: clinic.id,
      });
      handleClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao replicar agenda';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedProfessionals([]);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ContentCopy />
          <Typography variant="h6">Replicar Agenda</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          A agenda de <strong>{sourceProfessional.name}</strong> será copiada para os profissionais selecionados.
          Agendas existentes serão substituídas.
        </Alert>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Clínica: <Chip label={clinic.tradeName} size="small" />
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2">
            Selecione os profissionais:
          </Typography>
          <Button size="small" onClick={handleSelectAll}>
            {selectedProfessionals.length === targetProfessionals.length
              ? 'Limpar seleção'
              : 'Selecionar todos'}
          </Button>
        </Box>

        {targetProfessionals.length === 0 ? (
          <Alert severity="warning">
            Não há outros profissionais disponíveis para replicar a agenda.
          </Alert>
        ) : (
          <List sx={{ maxHeight: 300, overflow: 'auto' }}>
            {targetProfessionals.map((professional) => (
              <ListItem key={professional.id} disablePadding>
                <ListItemButton onClick={() => handleToggle(professional.id)} disabled={loading}>
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={selectedProfessionals.includes(professional.id)}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={professional.name}
                    secondary={professional.specialty}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {selectedProfessionals.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {selectedProfessionals.length} profissional(is) selecionado(s)
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleReplicate}
          disabled={loading || selectedProfessionals.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : <ContentCopy />}
        >
          {loading ? 'Replicando...' : 'Replicar Agenda'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleReplicationModal;
